"use client";

import { useMemo } from "react";
import { ResponsiveModal } from "@wtc/ui";
import { Button } from "@/components/ui/button";
import { MessageDetailPanel } from "@/components/crm/messages/MessageDetailPanel";
import type { TimelineItem } from "@/lib/hooks/api/timeline/enhanced-timeline";
import type { Message, ProviderKey } from "@/lib/core/crm/types";
import { ProviderKeySchema } from "@/lib/core/crm/types";
import { GenericTimelineItemDetail } from "./GenericTimelineItemDetail";
import { useHydratedCommunicationBody } from "@/lib/hooks/use-hydrated-communication-body";

interface TimelineItemDetailModalProps {
  item: TimelineItem | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCopyLink?: (item: TimelineItem) => void;
}

const resolveProviderKey = (provider: string | undefined): ProviderKey => {
  const parsed = ProviderKeySchema.safeParse(provider);
  return parsed.success ? parsed.data : "communication";
};

const transformTimelineItemToMessage = (
  item: TimelineItem,
  resolvedBody: string
): Message | null => {
  if (item.type !== "communication") return null;

  return {
    id: item.external_id || item.id,
    subject: item.title || "(No Subject)",
    body: resolvedBody,
    bodyPreview: resolvedBody.substring(0, 200) || item.body?.substring(0, 200) || "",
    sender: item.sender
      ? { name: item.sender.name || "Unknown", email: item.sender.email }
      : { name: "Unknown", email: "" },
    recipients: (item.recipients || []).map((r) => ({
      name: r.name || "Unknown",
      email: r.email,
    })),
    receivedDateTime: item.occurred_at,
    sentDateTime: item.occurred_at,
    isRead: item.status === "read" || item.status === "delivered",
    // `attachment_count` is the authoritative signal (the manifest can be absent
    // on a row whose count is known); fall back to the manifest length for
    // timeline items that carry no count, e.g. provider-sourced messages.
    hasAttachments: (item.attachment_count ?? item.attachments?.length ?? 0) > 0,
    importance: "normal" as const,
    conversationId: item.metadata?.threadId || item.id,
    webLink: item.metadata?.webLink,
    provider: resolveProviderKey(item.provider),
    providerMessageId: item.external_id || item.id,
    inboxId: item.connection_id || "",
  };
};

function CommunicationTimelineDetail({
  item,
  onOpenChange,
  onCopyLink,
}: {
  item: TimelineItem;
  onOpenChange: (open: boolean) => void;
  onCopyLink?: (item: TimelineItem) => void;
}) {
  const {
    body: resolvedBody,
    loading,
    error,
  } = useHydratedCommunicationBody({
    communicationId: item.id,
    // List `body` is the preview after offload — do not treat it as the full inline body.
    inlineBody: item.body_s3_key ? null : item.body,
    bodyS3Key: item.body_s3_key,
    previewSnippet: item.body,
  });

  const message = useMemo(
    () => transformTimelineItemToMessage(item, resolvedBody ?? item.body ?? ""),
    [item, resolvedBody]
  );

  if (!message) {
    return (
      <ResponsiveModal
        open
        onOpenChange={onOpenChange}
        title="Timeline Item Details"
        className="max-w-4xl h-[80vh]"
      >
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-muted-foreground">Unable to load message details</p>
            <Button variant="outline" onClick={() => onOpenChange(false)} className="mt-2">
              Close
            </Button>
          </div>
        </div>
      </ResponsiveModal>
    );
  }

  return (
    <ResponsiveModal
      open
      onOpenChange={onOpenChange}
      title={message.subject}
      className="max-w-4xl h-[80vh]"
    >
      <MessageDetailPanel
        message={message}
        onClose={() => onOpenChange(false)}
        className="h-full"
        showActions={true}
        // Keep the list preview visible while S3 hydrates; only block on empty.
        isLoading={loading && !resolvedBody}
        error={error && !resolvedBody ? error : null}
        onCopyLink={onCopyLink ? () => onCopyLink(item) : undefined}
      />
    </ResponsiveModal>
  );
}

export function TimelineItemDetailModal({
  item,
  isOpen,
  onOpenChange,
  onCopyLink,
}: TimelineItemDetailModalProps) {
  if (!item || !isOpen) return null;

  if (item.type === "communication") {
    return (
      <CommunicationTimelineDetail
        item={item}
        onOpenChange={onOpenChange}
        onCopyLink={onCopyLink}
      />
    );
  }

  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={onOpenChange}
      title="Timeline Item Details"
      className="max-w-3xl h-[80vh]"
    >
      <GenericTimelineItemDetail
        item={item}
        onClose={() => onOpenChange(false)}
        onCopyLink={onCopyLink}
      />
    </ResponsiveModal>
  );
}
